import {
  applyUserMaskRowToTabulator,
  safeParse,
  sanitizeTabulatorLayoutAttributes,
} from './tabulatorProgressiveLoading';

function collectLayoutFields(attributes) {
  const fields = [];
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      if (node.field) fields.push(String(node.field));
      if (Array.isArray(node.columns)) walk(node.columns);
    }
  };
  walk(attributes);
  return fields;
}

function flattenLayoutNodesByField(attributes) {
  const map = new Map();
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      if (node.field) map.set(String(node.field), node);
      if (Array.isArray(node.columns)) walk(node.columns);
    }
  };
  walk(attributes);
  return map;
}

function normalizeContractorIdLayoutNode(node) {
  return {
    ...(node && typeof node === 'object' ? node : {}),
    field: 'contractor_id',
    width: 0,
    minWidth: 0,
    maxWidth: 0,
    visible: false,
    print: false,
    download: false,
  };
}

/**
 * Zlúči uložený Tabulator column layout pre service_tasks tabuľky.
 *
 * - Layout **bez** poľa `id` → `null` (staré masky pred ID; neaplikovať poradie).
 * - Layout **s** `id` → zachová poradie z uložených attributes; canonical order len na
 *   doplnenie chýbajúcich polí a normalizáciu `contractor_id`.
 *
 * @param {unknown} savedAttributes
 * @param {readonly string[]} canonicalFieldOrder
 * @returns {object[]|null}
 */
export function mergeServiceTaskColumnLayoutAttributes(savedAttributes, canonicalFieldOrder, layoutSanitizeOptions) {
  const sanitized = sanitizeTabulatorLayoutAttributes(savedAttributes, layoutSanitizeOptions);
  if (!Array.isArray(sanitized) || sanitized.length <= 1) return null;

  const savedFields = collectLayoutFields(sanitized);
  if (!savedFields.includes('id')) return null;

  const canonicalSet = new Set(canonicalFieldOrder);
  const byField = flattenLayoutNodesByField(sanitized);
  const orderedNodes = [];
  const seen = new Set();

  const walkPreserveOrder = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      if (node.field) {
        const field = String(node.field);
        if (canonicalSet.has(field) && !seen.has(field)) {
          seen.add(field);
          if (field === 'contractor_id') {
            orderedNodes.push(normalizeContractorIdLayoutNode(byField.get(field) || node));
          } else {
            orderedNodes.push(byField.get(field) || node);
          }
        }
      }
      if (Array.isArray(node.columns)) walkPreserveOrder(node.columns);
    }
  };
  walkPreserveOrder(sanitized);

  if (!seen.has('contractor_id')) {
    const statusIdx = orderedNodes.findIndex((n) => n?.field === 'status_indicator');
    const insertAt = statusIdx >= 0 ? statusIdx + 1 : 0;
    orderedNodes.splice(insertAt, 0, normalizeContractorIdLayoutNode(byField.get('contractor_id')));
    seen.add('contractor_id');
  }

  for (const field of canonicalFieldOrder) {
    if (seen.has(field)) continue;
    if (field === 'contractor_id') {
      orderedNodes.push(normalizeContractorIdLayoutNode(byField.get(field)));
      seen.add(field);
      continue;
    }
    if (byField.has(field)) {
      orderedNodes.push(byField.get(field));
      seen.add(field);
      continue;
    }
    orderedNodes.push({ field });
    seen.add(field);
  }

  return orderedNodes.length > 1 ? orderedNodes : null;
}

/**
 * Aplikuje layout z user/standard masky na Tabulator tabuľku service_tasks.
 *
 * @param {object|null|undefined} table
 * @param {unknown} savedAttributes
 * @param {readonly string[]} canonicalFieldOrder
 */
export function applyServiceTaskColumnLayout(table, savedAttributes, canonicalFieldOrder, layoutSanitizeOptions) {
  if (!table) return;
  const layout = mergeServiceTaskColumnLayoutAttributes(savedAttributes, canonicalFieldOrder, layoutSanitizeOptions);
  if (!layout) return;
  try {
    table.setColumnLayout?.(layout);
  } catch (_) {}
}

/**
 * Obnoví user masku na service_tasks tabuľke: najprv layout (môže resetovať header filter DOM),
 * potom viditeľnosť, header filtre a triedenie do Tabulator UI.
 *
 * @param {object|null|undefined} table
 * @param {object|null|undefined} maskRow
 * @param {object} options
 * @param {readonly string[]} options.canonicalFieldOrder
 * @param {(v: object) => void} [options.setColumnsVisible]
 * @param {(table: object, columnsParsed: object, setColumnsVisible: function) => void} [options.applyColumnVisibilityFn]
 * @param {boolean} [options.clearHeaderFiltersWhenEmpty=true]
 */
export function restoreServiceTaskUserMaskToTable(table, maskRow, options = {}) {
  if (!table || !maskRow) return;
  const {
    canonicalFieldOrder,
    setColumnsVisible,
    applyColumnVisibilityFn,
    clearHeaderFiltersWhenEmpty = true,
    forceFrozenId = false,
  } = options;
  if (!canonicalFieldOrder) return;

  const layoutSanitizeOptions = forceFrozenId ? { forceFrozenId: true } : undefined;
  applyServiceTaskColumnLayout(table, safeParse(maskRow.attributes, []), canonicalFieldOrder, layoutSanitizeOptions);
  applyUserMaskRowToTabulator(table, maskRow, {
    applyLayout: false,
    setColumnsVisible,
    clearHeaderFiltersWhenEmpty,
    applyColumnVisibilityFn,
    applyFilters: true,
    applySort: true,
    forceFrozenId,
  });
}
